import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchBar } from '../components/SearchBar';
import {
  useMarketplace,
  type MarketplaceFilters,
  type MarketplaceProduct,
  type MarketplaceSortOption,
} from '../hooks/useMarketplace';
import { productUrl } from '../utils/slug';
import { SEO } from '../components/SEO';
import './MarketplacePage.css';
import { API_BASE } from '../config';

/* ─── Filter options ─── */

const POTENCY_OPTIONS = [
  { label: '1–2mg', value: '1-2' },
  { label: '3–5mg', value: '3-5' },
  { label: '6–10mg', value: '6-10' },
  { label: '10mg+', value: '11-100' },
];

const FORMAT_OPTIONS = [
  { label: 'Can', value: 'can' },
  { label: 'Bottle', value: 'bottle' },
  { label: 'Pouch', value: 'pouch' },
  { label: 'Box', value: 'box' },
  { label: 'Draft', value: 'draft' },
];

const FLAVOR_OPTIONS = [
  { label: 'Citrus', value: 'citrus' },
  { label: 'Berry', value: 'berry' },
  { label: 'Tropical', value: 'tropical' },
  { label: 'Herbal', value: 'herbal' },
  { label: 'Unflavored', value: 'unflavored' },
];

const ONSET_OPTIONS = [
  { label: '0–15 min', value: '0-15' },
  { label: '15–30 min', value: '15-30' },
  { label: '30–60 min', value: '30-60' },
  { label: '60+ min', value: '60-120' },
];

const SORT_OPTIONS: { label: string; value: MarketplaceSortOption }[] = [
  { label: 'Sponsored First', value: 'sponsored_first' },
  { label: 'Highest Rated', value: 'highest_rated' },
  { label: 'Lowest THC', value: 'lowest_thc' },
  { label: 'Highest THC', value: 'highest_thc' },
  { label: 'Newest', value: 'newest' },
  { label: 'Alphabetical', value: 'alphabetical' },
];

/* ─── Filter state ─── */

interface MarketplaceFilterState {
  potency: string | null;
  flavor: string | null;
  format: string | null;
  onsetTime: string | null;
  brand: string | null;
}

/* ─── Marketplace Product Card ─── */

interface MarketplaceCardProps {
  product: MarketplaceProduct;
}

function MarketplaceCard({ product }: MarketplaceCardProps) {
  const hasEnoughRatings = product.totalRatings >= 3;
  const hasAffiliateLink = !!product.affiliateLink;
  const checkPriceUrl = hasAffiliateLink
    ? `${API_BASE}/v1/go/${product.affiliateLink!.linkId}`
    : productUrl(product.productId, product.brandName, product.productName);

  return (
    <article
      className="marketplace-card"
      aria-label={`${product.productName} by ${product.brandName}`}
    >
      <div className="marketplace-card__image-container">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.productName}
            className="marketplace-card__image"
            loading="lazy"
          />
        ) : (
          <div className="marketplace-card__image-placeholder" aria-hidden="true">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
        <span className="marketplace-card__thc-badge">
          {product.thcContentMg}mg THC
        </span>
        {product.sponsored && (
          <span className="marketplace-card__sponsored-label">Sponsored</span>
        )}
      </div>

      <div className="marketplace-card__info">
        <p className="marketplace-card__brand">{product.brandName}</p>
        <h3 className="marketplace-card__name">{product.productName}</h3>

        <div className="marketplace-card__scores">
          {hasEnoughRatings ? (
            <>
              <span
                className="marketplace-card__rating"
                aria-label={`Rating: ${product.aggregateScore} out of 5`}
              >
                ★ {product.aggregateScore?.toFixed(1)}
              </span>
              {product.aggregateBuzzScore != null && (
                <span
                  className="marketplace-card__buzz"
                  aria-label={`Buzz score: ${product.aggregateBuzzScore} out of 10`}
                >
                  ⚡ {product.aggregateBuzzScore.toFixed(1)}
                </span>
              )}
            </>
          ) : (
            <span className="marketplace-card__no-ratings">Not enough ratings</span>
          )}
        </div>

        <div className="marketplace-card__cta-area">
          <a
            href={checkPriceUrl}
            className={`marketplace-card__cta${hasAffiliateLink ? '' : ' marketplace-card__cta--secondary'}`}
            target={hasAffiliateLink ? '_blank' : undefined}
            rel={hasAffiliateLink ? 'noopener noreferrer' : undefined}
          >
            {hasAffiliateLink ? 'Check Price on Brand Site' : 'View Product'}
          </a>
          {product.discountCode && (
            <span className="marketplace-card__discount">
              {product.discountDescription || `Use code ${product.discountCode}`}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/* ─── Main Page Component ─── */

export function MarketplacePage() {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [sort, setSort] = useState<MarketplaceSortOption>('sponsored_first');
  const [filterState, setFilterState] = useState<MarketplaceFilterState>({
    potency: null,
    flavor: null,
    format: null,
    onsetTime: null,
    brand: null,
  });

  const sentinelRef = useRef<HTMLDivElement>(null);

  // Build filters for the hook
  const filters: MarketplaceFilters = {
    query: searchQuery,
    potency: filterState.potency,
    flavor: filterState.flavor,
    format: filterState.format,
    onsetTime: filterState.onsetTime,
    brand: filterState.brand,
    sort,
  };

  const { products, loading, loadingMore, error, hasMore, totalCount, loadMore } =
    useMarketplace(filters);

  // Search handlers
  const handleSearch = useCallback((query: string) => {
    const trimmed = query.trim();
    if (query.length > 0 && trimmed.length === 0) {
      setSearchError('Please enter a valid search term');
      setSearchQuery('');
      return;
    }
    setSearchError(null);
    setSearchQuery(trimmed);
  }, []);

  const handleInputChange = useCallback((value: string) => {
    setSearchInput(value);
    if (value.trim().length > 0 || value.length === 0) {
      setSearchError(null);
    }
  }, []);

  // Filter pill toggle (single-select per category)
  const handleFilterToggle = useCallback(
    (category: keyof MarketplaceFilterState, value: string) => {
      setFilterState((prev) => ({
        ...prev,
        [category]: prev[category] === value ? null : value,
      }));
    },
    []
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilterState({ potency: null, flavor: null, format: null, onsetTime: null, brand: null });
    setSearchInput('');
    setSearchQuery('');
    setSearchError(null);
  }, []);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  const hasActiveFilters =
    !!searchQuery ||
    !!filterState.potency ||
    !!filterState.flavor ||
    !!filterState.format ||
    !!filterState.onsetTime ||
    !!filterState.brand;

  const showNoResults = !loading && !error && products.length === 0 && hasActiveFilters;

  return (
    <div className="marketplace-page">
      <SEO
        title="THC Beverage Marketplace - Buy Cannabis Drinks"
        description="Browse THC beverages with real-time pricing, affiliate deals, and discount codes. Buy directly from brands with exclusive offers."
        canonical="/buy-thc-drinks"
      />
      {/* Search */}
      <SearchBar
        value={searchInput}
        onChange={handleInputChange}
        onSearch={handleSearch}
        error={searchError}
      />

      {/* Filter pills + Sort */}
      <div className="marketplace-page__controls">
        <div className="marketplace-page__filters" role="group" aria-label="Marketplace filters">
          <div className="marketplace-page__filters-row">
            {POTENCY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`marketplace-page__pill${filterState.potency === opt.value ? ' marketplace-page__pill--active' : ''}`}
                onClick={() => handleFilterToggle('potency', opt.value)}
                aria-pressed={filterState.potency === opt.value}
              >
                {opt.label}
              </button>
            ))}

            <span className="marketplace-page__divider" aria-hidden="true" />

            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`marketplace-page__pill${filterState.format === opt.value ? ' marketplace-page__pill--active' : ''}`}
                onClick={() => handleFilterToggle('format', opt.value)}
                aria-pressed={filterState.format === opt.value}
              >
                {opt.label}
              </button>
            ))}

            <span className="marketplace-page__divider" aria-hidden="true" />

            {ONSET_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`marketplace-page__pill${filterState.onsetTime === opt.value ? ' marketplace-page__pill--active' : ''}`}
                onClick={() => handleFilterToggle('onsetTime', opt.value)}
                aria-pressed={filterState.onsetTime === opt.value}
              >
                {opt.label}
              </button>
            ))}

            <span className="marketplace-page__divider" aria-hidden="true" />

            {FLAVOR_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`marketplace-page__pill${filterState.flavor === opt.value ? ' marketplace-page__pill--active' : ''}`}
                onClick={() => handleFilterToggle('flavor', opt.value)}
                aria-pressed={filterState.flavor === opt.value}
              >
                {opt.label}
              </button>
            ))}

            <span className="marketplace-page__divider" aria-hidden="true" />

            <input
              type="text"
              className={`marketplace-page__pill marketplace-page__pill-input${filterState.brand ? ' marketplace-page__pill--active' : ''}`}
              placeholder="Brand..."
              value={filterState.brand || ''}
              onChange={(e) =>
                setFilterState((prev) => ({
                  ...prev,
                  brand: e.target.value || null,
                }))
              }
              aria-label="Filter by brand"
            />
          </div>
        </div>

        <div className="marketplace-page__sort">
          <label htmlFor="marketplace-sort" className="marketplace-page__sort-label">
            Sort:
          </label>
          <select
            id="marketplace-sort"
            className="marketplace-page__sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value as MarketplaceSortOption)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Result count */}
      {!loading && products.length > 0 && (
        <p className="marketplace-page__count" aria-live="polite">
          {totalCount} product{totalCount !== 1 ? 's' : ''} found
        </p>
      )}

      {/* Error */}
      {error && (
        <div className="marketplace-page__error" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/* No results */}
      {showNoResults && (
        <div className="marketplace-page__empty" role="status">
          <p className="marketplace-page__empty-title">No products match your filters</p>
          <p className="marketplace-page__empty-subtitle">
            Try removing one or more filters to broaden your results.
          </p>
          <button
            type="button"
            className="marketplace-page__clear-btn"
            onClick={clearFilters}
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Product grid */}
      {products.length > 0 && (
        <div className="marketplace-page__grid" role="feed" aria-label="Marketplace products">
          {products.map((product) => (
            <MarketplaceCard key={product.productId} product={product} />
          ))}
        </div>
      )}

      {/* Loading states */}
      {loading && (
        <div className="marketplace-page__loading" aria-label="Loading products">
          <div className="marketplace-page__spinner" />
        </div>
      )}

      {loadingMore && (
        <div className="marketplace-page__loading-more" aria-label="Loading more products">
          <div className="marketplace-page__spinner marketplace-page__spinner--small" />
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="marketplace-page__sentinel" aria-hidden="true" />
    </div>
  );
}
